import type { WebviewMedia } from '@amzn/bf-playback'
import { DeviceType, onLine } from '@amazon/vinyl-util'
import { getDeviceConfig } from '@/device/deviceConfig'

/** The native engine's exclusive output mode, so no other app can play on the device. */
export interface NativeExclusiveMode {
    /** Whether the engine can take exclusive access on this platform. */
    supported(): Promise<boolean>
    /** Takes or releases exclusive access; resolves whether the engine holds it now. */
    set(enabled: boolean): Promise<boolean>
    /**
     * Lets go of the device at once, unless the engine plays: playback moved to the webview's own
     * media. Older native builds' shims have none; their engines let go when nothing plays.
     */
    release?(): void
    /** Calls `listener` when the mode ends unasked: another app held the device at play, or it went away. */
    onEnd(listener: () => void): void
}

/** An output device as the native engine lists it; `id` is stable across launches and replugs. */
export interface NativeOutputDevice {
    readonly id: string
    readonly name: string
    /** The system's default output device. */
    readonly default: boolean
}

/** The native engine's output devices and the one it plays on; `selected: null` follows the default. */
export interface NativeOutputDeviceList {
    readonly devices: readonly NativeOutputDevice[]
    readonly selected: string | null
}

/** The output device the native engine plays on, chosen among the system's. */
export interface NativeOutputDevices {
    /** Whether the engine lists and selects output devices. */
    supported(): Promise<boolean>
    /** The devices and the selection; from then on the engine reports their changes to `onChange`. */
    list(): Promise<NativeOutputDeviceList>
    /** Plays on the device `id` names, or the system default with `null`. */
    select(id: string | null): Promise<NativeOutputDeviceList>
    /** Calls `listener` when a device comes or goes, or the selected one went away. */
    onChange(listener: (list: NativeOutputDeviceList) => void): void
}

/** The device the native engine plays on now: the rate it plays at and the highest it supports, in Hz. */
export interface NativeOutputFormat {
    readonly name: string
    readonly sampleRate: number
    readonly maxSampleRate: number
}

declare global {
    interface Window {
        /** bf-native-mse's shim, injected by the desktop shell when it ships the native engine. */
        __nativeMse?: {
            install(): unknown
            readonly switchesQuality?: true
            readonly appliesGain?: true
            readonly playsShaka?: true
            readonly playsFiles?: true
            readonly exclusiveMode?: NativeExclusiveMode
            readonly outputDevices?: NativeOutputDevices
            readonly outputFormat?: () => Promise<NativeOutputFormat | null>
        }
    }
}

/** Read before install(): an OTA bundle also runs on shells whose shim install() returns nothing. */
function ownWebviewMedia(): WebviewMedia {
    return {
        MediaSource: window.MediaSource,
        ManagedMediaSource: (window as { ManagedMediaSource?: typeof MediaSource })
            .ManagedMediaSource,
        requestMediaKeySystemAccess: navigator.requestMediaKeySystemAccess.bind(navigator),
    }
}

const state: {
    webview: WebviewMedia | null
    switchesQuality: boolean
    appliesGain: boolean
    playsShaka: boolean
    playsFiles: boolean
    exclusiveMode: NativeExclusiveMode | null
    outputDevices: NativeOutputDevices | null
    outputFormat: (() => Promise<NativeOutputFormat | null>) | null
} = {
    webview: null,
    switchesQuality: false,
    appliesGain: false,
    playsShaka: false,
    playsFiles: false,
    exclusiveMode: null,
    outputDevices: null,
    outputFormat: null,
}

/**
 * In the desktop app with the native engine, hands audio playback to it: the engine's
 * MediaSource, media element and EME replace the webview's, so catalog plays as DASH with
 * Widevine through the engine's CDM, the CDM downloads use too. Video keeps the webview's own
 * media stack ({@link webviewMedia}). Elsewhere it does nothing. Call before any playback engine
 * is created.
 */
export function installNativePlayback(): boolean {
    const config = getDeviceConfig()
    const shim = window.__nativeMse
    if (state.webview || !config.installed || config.deviceType !== DeviceType.DESKTOP || !shim) {
        return nativePlaybackInstalled()
    }
    const webview = ownWebviewMedia()
    shim.install()
    state.webview = webview
    state.switchesQuality = shim.switchesQuality === true
    state.appliesGain = shim.appliesGain === true
    state.playsShaka = shim.playsShaka === true
    state.playsFiles = shim.playsFiles === true
    state.exclusiveMode = shim.exclusiveMode ?? null
    state.outputDevices = shim.outputDevices ?? null
    state.outputFormat = shim.outputFormat ?? null
    return true
}

/** Whether the native engine plays HD streaming's mid-song quality switches; older builds' don't. */
export function nativeEngineSwitchesQuality(): boolean {
    return state.switchesQuality
}

/** Whether the native engine plays an element's volume as gain, as loudness normalization needs; older builds' don't. */
export function nativeEngineAppliesGain(): boolean {
    return state.appliesGain
}

/** The native engine's exclusive output mode; older native builds' shims have none. */
export function nativeExclusiveMode(): NativeExclusiveMode | undefined {
    return state.exclusiveMode ?? undefined
}

/** The native engine's output device selection; older native builds' shims have none. */
export function nativeOutputDevices(): NativeOutputDevices | undefined {
    return state.outputDevices ?? undefined
}

/**
 * The device the native engine plays on now and its rates; `null` with no device. Older native
 * builds' shims have none.
 */
export function nativeOutputFormat(): (() => Promise<NativeOutputFormat | null>) | undefined {
    return state.outputFormat ?? undefined
}

/** Whether a Shaka player (the Audible library's) plays on the native engine; older builds' can't. */
export function nativeEnginePlaysShaka(): boolean {
    return state.playsShaka
}

/** Whether the native engine plays a computer's own files (`bf-local:` sources); older builds' can't. */
export function nativeEnginePlaysFiles(): boolean {
    return state.playsFiles
}

/** Whether audio plays on the native engine, which downloads need. */
export function nativePlaybackInstalled(): boolean {
    return state.webview != null
}

/** The webview's own MSE and EME while the native engine's are the page's, for video. */
export function webviewMedia(): WebviewMedia | undefined {
    return state.webview ?? undefined
}

/** Downloads exist only in the desktop app running the native engine, never on the web. */
export const downloadsSupported = nativePlaybackInstalled

/** The page an offline launch opens instead of home. */
export const OFFLINE_HOME = '/my/downloads'

/**
 * A launch with no network opens Downloads instead of home, which needs the network; Downloads
 * reads only the device. Call before the router mounts.
 */
export function redirectOfflineLaunch(): void {
    if (!downloadsSupported() || onLine() || location.pathname !== '/') return
    history.replaceState(history.state, '', OFFLINE_HOME)
}
